import { useLayoutEffect, useState, type RefObject, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useOverflowPortal } from './context';
import { layers } from 'app/spacing';

export interface OverflowPortalProps {
  triggerRef: RefObject<HTMLElement>;
  children: ReactNode;
  enabled?: boolean;
}

export const OverflowPortal = ({
  triggerRef,
  children,
  enabled = true,
}: OverflowPortalProps) => {
  const portalContainer = useOverflowPortal();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null
  );

  useLayoutEffect(() => {
    // Calculate and update portal position based on trigger element's screen coordinates
    // Handles ref timing - waits for ref to be set if not immediately available
    if (!enabled || !portalContainer) {
      return;
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.top,
          left: rect.left,
        });
      }
    };

    if (!triggerRef.current) {
      let rafId: number;
      const checkRef = () => {
        if (triggerRef.current) {
          updatePosition();
        } else {
          rafId = requestAnimationFrame(checkRef);
        }
      };
      rafId = requestAnimationFrame(checkRef);
      return () => cancelAnimationFrame(rafId);
    }

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [enabled, triggerRef, portalContainer]);

  if (!enabled) {
    return <>{children}</>;
  }

  if (!portalContainer) {
    return <>{children}</>;
  }

  if (!position) {
    return null;
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: layers.overlay,
      }}
    >
      {children}
    </div>,
    portalContainer
  );
};
